import type { ComponentRecord } from "../../types/components";
import { useUnlinkComponent } from "../../hooks/useComponents";
import { VerifiedPendingBadge } from "./VerifiedPendingBadge";

interface Props {
  projectId: string;
  component: ComponentRecord | null;
}

export function ComponentDetailPanel({ projectId, component }: Props) {
  const unlink = useUnlinkComponent(projectId);

  if (!component) {
    return (
      <aside
        data-testid="component-detail-empty"
        style={{
          border: "1px dashed #d1d5db",
          borderRadius: 8,
          padding: "1.5rem",
          color: "#6b7280",
        }}
      >
        Select a component on the left to view its full specifications and
        provenance.
      </aside>
    );
  }

  const provenance = renderProvenance(component);
  const rows = renderSpecRows(component);

  return (
    <aside
      data-testid={`component-detail-${component.id}`}
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "1.25rem",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <h3 style={{ margin: 0 }}>{component.manufacturer}</h3>
          <div style={{ color: "#374151" }}>{component.model_number}</div>
          <div style={{ marginTop: "0.4rem" }}>
            <VerifiedPendingBadge component={component} />
          </div>
        </div>
        <span
          title={provenance}
          data-testid="provenance-badge"
          style={{
            background: "#eff6ff",
            color: "#1e40af",
            padding: "0.2rem 0.6rem",
            borderRadius: 999,
            fontSize: "0.75rem",
            alignSelf: "flex-start",
          }}
        >
          provenance
        </span>
      </div>

      <table
        data-testid="component-spec-table"
        style={{
          marginTop: "1rem",
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.85rem",
        }}
      >
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <td
                style={{
                  padding: "0.25rem 0.5rem",
                  color: "#6b7280",
                  width: "55%",
                }}
              >
                {label}
              </td>
              <td style={{ padding: "0.25rem 0.5rem" }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
        <button
          type="button"
          data-testid="component-detail-unlink"
          disabled={unlink.isPending}
          onClick={() => unlink.mutate(component.id)}
          style={{
            background: "transparent",
            border: "1px solid #d1d5db",
            padding: "0.4rem 0.75rem",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {unlink.isPending ? "Unlinking…" : "Unlink"}
        </button>
      </div>
    </aside>
  );
}

function renderProvenance(component: ComponentRecord): string {
  if (component.source === "seed") {
    return "Curated from the SunWave reference catalog. Placeholder values pending verification.";
  }
  const meta = component.extraction_metadata ?? {};
  const filename = meta.source_pdf_filename;
  const model = meta.model_used;
  if (filename || model) {
    return `Extracted${filename ? ` from ${filename}` : ""}${model ? ` · model ${model}` : ""}.`;
  }
  return `Source: ${component.source}. Confidence: ${component.confidence}.`;
}

function renderSpecRows(component: ComponentRecord): [string, string][] {
  const s = component.spec_data;
  if (component.component_type === "module") {
    return [
      ["Series", s.model_series as string],
      ["Technology", s.technology as string],
      ["Pmp", `${s.pmp_w} W`],
      ["Vmp / Voc", `${s.vmp_v} V / ${s.voc_v} V`],
      ["Imp / Isc", `${s.imp_a} A / ${s.isc_a} A`],
      ["Efficiency", `${s.efficiency_pct}%`],
      ["Dimensions", `${s.length_mm} × ${s.width_mm} × ${s.thickness_mm} mm`],
      ["Weight", `${s.weight_kg} kg`],
      ["Cells", `${s.cell_count}`],
      ["Bifacial", (s.is_bifacial as boolean) ? "yes" : "no"],
      ["Max system V", `${s.max_system_voltage_v} V`],
    ].filter((row): row is [string, string] => {
      const [, v] = row;
      return v !== undefined && v !== null && v !== "";
    });
  }
  if (component.component_type === "inverter") {
    return [
      ["Series", s.model_series as string],
      ["Type", s.inverter_type as string],
      ["AC power", `${((s.rated_ac_power_w as number) / 1000).toFixed(0)} kW`],
      ["DC power", `${((s.max_dc_power_w as number) / 1000).toFixed(0)} kW`],
      ["Max DC V", `${s.max_dc_voltage_v} V`],
      ["MPPT range", `${s.mppt_voltage_min_v}-${s.mppt_voltage_max_v} V`],
      ["MPPTs", `${s.mppt_count}`],
      ["Nominal AC V", `${s.nominal_ac_voltage_v} V`],
      ["Frequency", `${s.frequency_hz} Hz`],
    ].filter((row): row is [string, string] => {
      const [, v] = row;
      return v !== undefined && v !== null && v !== "";
    });
  }
  return [
    ["Series", s.model_series as string],
    ["Type", s.tracker_type as string],
    ["Table size", `${s.table_size_modules} modules`],
    ["Chord length", `${s.chord_length_m} m`],
    ["Tracking range", `±${s.tracking_range_deg}°`],
    ["Module orientation", s.module_orientation as string],
    ["Pile reveal", `${s.min_pile_reveal_m}-${s.max_pile_reveal_m} m`],
    [
      "Max slope (N/S/EW)",
      `${s.max_slope_north_pct}% / ${s.max_slope_south_pct}% / ${s.max_slope_east_west_pct}%`,
    ],
    ["Backtracking", (s.supports_backtracking as boolean) ? "yes" : "no"],
  ].filter((row): row is [string, string] => {
    const [, v] = row;
    return v !== undefined && v !== null && v !== "";
  });
}
